import { SLOT_LABEL, type SlotState } from './programSlots';

/**
 * The words for a lesson's state, in one place.
 *
 * They used to live twice: WaitingForLesson hardcoded the single lesson's
 * sentences and BundleProgress carried the program's. Renaming the status label
 * to "Awaiting analysis" reached one file and not the other, so the label said
 * "analysis" directly above a paragraph saying "reviewing", and the program
 * overview and the program's own active lesson said different things about the
 * same state. One source, both callers.
 *
 * `lessonNumber` is what separates the two callers: a program names the lesson
 * it is talking about, a single lesson has nothing to name. Passing it as an
 * option rather than forking the sentence keeps the shared clause shared.
 *
 * `long` is the desktop paragraph and `short` the mobile one. The short form
 * carries only the timing, because the label above it already says what is
 * happening and a narrow screen has no room to repeat it.
 *
 * Footage is what the player sends; a lesson is what the coach delivers. The
 * two are never the same noun here — nothing "uploads a lesson" and no lesson
 * is "being analyzed", the footage is.
 */

export interface LessonStateCopy {
  label: string;
  long: string;
  short: string;
}

/**
 * The coach's names for the same states. Separate strings, not a rewording of
 * the player's: "Awaiting analysis" describes waiting for someone else, and the
 * coach IS that someone. These are the labels the coach's list rows already
 * use, so one state has one name on their side too.
 */
const COACH_LABEL: Partial<Record<SlotState, string>> = {
  ready_to_submit: 'Awaiting upload',
  uploading: 'Processing video...',
  processing: 'Processing video...',
  awaiting_review: 'Awaiting your review',
  ready_to_watch: 'Delivered',
  complete: 'Delivered',
  refunded: 'Refunded',
};

export function lessonStateCopy(
  state: SlotState,
  opts: {
    coachFirstName: string;
    lessonNumber?: number;
    totalLessons?: number;
    /** Whose screen this is. Defaults to the player's. */
    viewer?: 'player' | 'coach';
    /** The player's first name. Required for coach copy. */
    playerFirstName?: string;
  },
): LessonStateCopy | null {
  const { coachFirstName: coach, lessonNumber: n, totalLessons: total } = opts;

  if (opts.viewer === 'coach') {
    const label = COACH_LABEL[state];
    if (!label) return null;
    const player = opts.playerFirstName || 'Your player';
    const forLesson = n != null ? ` lesson ${n}` : ' this lesson';
    switch (state) {
      case 'awaiting_review':
        return {
          label,
          long: `${player} submitted${forLesson}. Record your breakdown when you are ready.`,
          short: `${player} submitted${forLesson}`,
        };
      case 'ready_to_submit':
        return {
          label,
          long: `Waiting on ${player} to send footage for${forLesson}.`,
          short: `Waiting on ${player}`,
        };
      case 'uploading':
      case 'processing':
        return {
          label,
          long: `${player}'s footage for${forLesson} is still processing. It reaches you when that finishes.`,
          short: 'Processing',
        };
      case 'ready_to_watch':
      case 'complete':
        return {
          label,
          long: total != null && n != null && n >= total
            ? `All ${total} lessons delivered. The progress report is the last thing this program needs.`
            : `Lesson ${n} delivered. ${player} can watch it now.`,
          short: n != null ? `Lesson ${n} delivered` : 'Delivered',
        };
      case 'refunded':
        return {
          label,
          long: 'A refund was requested. Lessons already delivered stay available to both of you.',
          short: 'Refund requested',
        };
      default:
        return null;
    }
  }

  /** " for lesson 3" on a program, nothing on a single lesson. */
  const forLesson = n != null ? ` for lesson ${n}` : '';

  switch (state) {
    case 'awaiting_review':
      return {
        label: SLOT_LABEL.awaiting_review,
        long: `${coach} is analyzing your footage. Breakdowns usually come back within 1 to 2 days, and you'll be notified as soon as it's ready.`,
        short: `${coach} typically delivers within 1-2 days`,
      };
    case 'ready_to_submit':
      return {
        label: SLOT_LABEL.ready_to_submit,
        long: `${coach} is ready when you are. Upload your footage${forLesson}.`,
        short: `Upload your footage${forLesson}`,
      };
    case 'ready_to_watch':
      return {
        label: SLOT_LABEL.ready_to_watch,
        long: n != null && total != null && n < total
          ? `Lesson ${n} is ready below. You can send footage for lesson ${n + 1} once you've watched it.`
          : n != null && total != null
            ? `Lesson ${n} is ready below. It was the last of ${total}.`
            : 'Your breakdown is ready below.',
        short: n != null ? `Lesson ${n} is ready below` : 'Your breakdown is ready below',
      };
    case 'uploading':
      return {
        label: SLOT_LABEL.uploading,
        long: `Your footage${forLesson} is uploading. Keep this tab open until it finishes; after that you can close it.`,
        short: 'Keep this tab open until the upload finishes',
      };
    case 'processing':
      return {
        label: SLOT_LABEL.processing,
        long: `Your footage is processing. It reaches ${coach} when that finishes, and you can close this tab.`,
        short: `It reaches ${coach} when processing finishes`,
      };
    case 'complete':
      return {
        label: SLOT_LABEL.complete,
        long: total != null
          ? `All ${total} lessons delivered. They stay here for you to rewatch.`
          : 'Delivered. It stays here for you to rewatch.',
        short: total != null ? `All ${total} lessons delivered` : 'Delivered',
      };
    case 'refunded':
      return {
        label: SLOT_LABEL.refunded,
        long: 'A refund was requested. Lessons already delivered stay available below.',
        short: 'Lessons already delivered stay available below',
      };
    default:
      return null;
  }
}
